// #genai Phase 2 "Add Bank Payment Method" route styles.
// Pixel-matched to Figma 269:20728 (Corporate) / 269:20973 (Personal) / 269:21136 (Savings).
// Rendered at /billing-information/add-ach behind the ENABLE_MULTI_PAYMENT_METHODS flag.
// Two numbered sections only (Payment Information / Billing Information) — the payment-type
// chooser was removed in the new design (the Bank vs Card choice now lives in the list dropdown).
import { makeStyles } from 'lib/components';

export default makeStyles((theme) => ({
  // #genai Mobile (Figma 269:21887): the form must fill the page's <main> column so the white card
  // can stretch the full height and the Save/Cancel footer can anchor to the bottom (the buttons
  // sit just above the global Terms/Privacy footer, not directly under the form content). Desktop is
  // untouched (default block flow keeps the card's centered margin layout).
  formRoot: {
    '@media (max-width: 639px)': {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
  },

  // White card — bg white, gap 24, padding 24, radius 4, ev-elevation--z01 shadow.
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    margin: '20px',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.12), 0px 0px 2px 0px rgba(0,0,0,0.14)',
    // #genai Phase 2 inline error styling (Figma 269:21936 / 269:22011): errored MUI inputs (Account
    // Type select, name + address fields) get a #BC1616 border + #FFCDD1 fill. Labels/helpers are
    // already red via the field styles; hosted-field iframes use `hostedFieldError`.
    '& .myev-MuiOutlinedInput-root.myev-Mui-error': {
      backgroundColor: '#FFCDD1',
    },
    '& .myev-MuiOutlinedInput-root.myev-Mui-error .myev-MuiOutlinedInput-notchedOutline': {
      borderColor: '#BC1616',
    },
    // #genai Mobile (Figma 226:31979 / 226:32388): full-bleed flat surface — no shadow, no margin,
    // no radius. Padding shrinks to 16/24 (vertical/horizontal) and the gap is owned by the section
    // wrappers below so Figma's hairline dividers can sit flush between groups.
    '@media (max-width: 639px)': {
      margin: '0px',
      padding: '0px',
      gap: '0px',
      borderRadius: '0px',
      boxShadow: 'none',
      // #genai Grow to fill <main> so the white surface runs full-height and the footer (margin-top
      // auto below) drops to the bottom of the viewport per Figma 269:21887.
      flexGrow: 1,
    },
  },

  // #genai Mobile sub-header (Figma 269:21887 / 269:21702): back-arrow on the left + "Add Payment
  // Method" title centered in the full width (no X close icon in the new design). White bg, 16/24
  // padding, full bleed. Hidden on desktop (already provided by the parent app chrome).
  mobileHeader: {
    display: 'none',
    '@media (max-width: 639px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
    },
  },

  // #genai Mobile-header back button — 24px hit target, charcoal-900 glyph, no background. Absolutely
  // positioned on the left so the title stays centered in the full header width (Figma 269:21887).
  mobileHeaderIconBtn: {
    position: 'absolute',
    left: '24px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#231F20',
    cursor: 'pointer',
    '& svg': {
      width: '24px',
      height: '24px',
    },
    '&:hover': {
      opacity: 0.7,
    },
  },

  // #genai Mobile-header title — Noto Sans Bold 16/24, charcoal-900.
  mobileHeaderTitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    color: '#231F20',
  },

  // Inner sections column — gap 24, max width 800 per Figma.
  // #genai Mobile (Figma 226:31979): the wrapper sheds its gap so each group below can own its
  // own padding / border-bottom; horizontal padding 24 is applied directly here.
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '800px',
    '@media (max-width: 639px)': {
      gap: '0px',
      maxWidth: 'none',
    },
  },

  // #genai Mobile-only grouper for Section 1 (Choose Payment Type) + Section 2 (Payment Info)
  // (Figma 226:31986). On desktop it's transparent (display: contents) so it doesn't affect the
  // sections-wrapper's existing flex layout. On mobile it becomes a flex-column with 24px gap,
  // 16/24 padding, and a 1px #DCE0E9 hairline below it that divides this group from Billing.
  mobilePaymentGroup: {
    display: 'contents',
    '@media (max-width: 639px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '16px 24px',
      borderBottom: '1px solid #DCE0E9',
      boxSizing: 'border-box',
      width: '100%',
    },
  },

  // #genai State 1 (Figma 269:21887) shows a continuous white surface with NO hairline under the
  // Account Type dropdown — the only divider is the global footer's. The payment group's bottom
  // border is only correct in State 2 (it separates Payment Information from Billing), so drop it
  // here when the full form isn't shown.
  mobilePaymentGroupNoBorder: {
    '@media (max-width: 639px)': {
      borderBottom: 'none',
    },
  },

  // #genai Mobile-only grouper for Section 3 (Billing Address) + Make-default checkbox
  // (Figma 226:32002). display: contents on desktop; flex group with padding on mobile.
  mobileBillingGroup: {
    display: 'contents',
    '@media (max-width: 639px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '16px 24px',
      borderBottom: '1px solid #DCE0E9',
      boxSizing: 'border-box',
      width: '100%',
    },
  },

  // "1) Choose Payment Type" / "2) Payment Information" / "3) Billing Information"
  sectionHeader: {
    fontFamily: 'Noto Sans',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '27px',
    color: '#005A8D',
  },

  // "3) Billing Information" header + subtitle grouped tightly (gap 6).
  billingHeaderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  // Subtitle under "3) Billing Information".
  billingSubtitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#4D5365',
  },

  // Indented sub-section under each numbered header (pl-24).
  subSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingLeft: '24px',
    // #genai Mobile (Figma 226:31979): fields run flush to the card edge (no indent). The shared
    // AchPaymentInformation/AddressInformation components apply inline `maxWidth: 383px / 782px`
    // on their grid items for the desktop two-column layout — we override those to full-width on
    // mobile so every input takes the full row per Figma. !important is required to win over the
    // inline style.
    '@media (max-width: 639px)': {
      paddingLeft: '0px',
      '& .myev-MuiGrid-item': {
        maxWidth: 'none !important',
      },
    },
  },

  // Wrapper around the reused AddressInformation. Two scoped-only overrides (no change to the
  // shared component / Phase 1):
  //   1) hide its built-in Save/Cancel button row (this page renders its own footer)
  //   2) lay the address fields out as Figma's 2-column billing grid so Primary Phone and
  //      Secondary phone sit side-by-side instead of stacked.
  addressWrap: {
    '& #submitOrderBtn, & [data-testid="cancelButton"]': {
      display: 'none',
    },
    '& > .myev-MuiGrid-root': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: '32px',
      rowGap: '24px',
      maxWidth: '776px',
    },
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root': {
      gridColumn: '1 / span 2',
      marginBottom: 0,
    },
    '& .myev-MuiGrid-container .myev-MuiGrid-item': {
      marginBottom: 0,
    },
    // #genai All billing field labels are Noto Sans SemiBold 16/24 in Figma 269:20728. The shared
    // AddressInformation renders them at regular weight, so bump weight/line-height here (scoped to
    // this page only — Phase 1 is untouched).
    '& .myev-MuiFormLabel-root': {
      fontWeight: 600,
      lineHeight: '24px',
    },
    // #genai Company before Billing email (Figma 269:20728) now comes from AddressInformation's own
    // Phase 2 source order, so tab order matches the visual order — no CSS `order` swap here.
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(4)': {
      gridColumn: '1',
    },
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(5)': {
      gridColumn: '2',
    },
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(8)': {
      display: 'none',
    },
    '@media (max-width: 639px)': {
      '& > .myev-MuiGrid-root': {
        gridTemplateColumns: '1fr',
      },
      '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(4)': {
        gridColumn: '1',
      },
      '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(5)': {
        gridColumn: '1',
      },
    },
  },

  // #genai State 1 dropdown block bottom padding (Figma 269:20537 has pb-24). Combined with the
  // card's 24px gap to the footer this yields the exact 48px gap between the dropdown and the
  // Add Payment Method button. Mobile owns its spacing via the group wrappers, so it's reset there.
  accountTypeBlock: {
    paddingBottom: '24px',
    '@media (max-width: 639px)': {
      paddingBottom: '0px',
    },
  },

  // #genai State 1 Account Type label (Figma 269:20551 — Noto Sans SemiBold 16/24, #231F20 with a
  // #BC1616 asterisk). Distinct from the regular billing labels which are charcoal-800 / regular.
  // Phase 2 field-error UX: labels stay charcoal on error (no `Mui-error` overrides).
  accountTypeLabel: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#231F20',
    marginBottom: '8px',
    width: 'fit-content',
  },

  // #genai State 1 Account Type dropdown (Figma 269:20516) — 380px wide, 44px tall, radius 4, 16px
  // text, #82899C (charcoal-600) border to match the Figma Control exactly.
  accountTypeSelect: {
    maxWidth: '380px',
    borderRadius: 4,
    fontSize: 16,
    '& .myev-MuiOutlinedInput-input': {
      padding: '10.5px 14px',
    },
    '& .myev-MuiOutlinedInput-notchedOutline': {
      borderColor: '#82899C',
    },
  },

  // #genai First Name + Last Name row in Billing Information (Figma 269:20728, frame 590:14521 —
  // names moved out of Payment Information). Two equal 380px columns with a 16px gap (total 776px,
  // matching the billing content width); stacks full-width on mobile.
  nameRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    maxWidth: '776px',
    '& > *': {
      flex: 1,
    },
    '@media (max-width: 639px)': {
      flexDirection: 'column',
      gap: '24px',
      maxWidth: 'none',
    },
  },

  // #genai Field label for the name row — Noto Sans SemiBold 16/24 #4D5365, matching every other
  // billing field label in Figma 269:20728 (590:14523 / 590:14525). Phase 2 field-error UX:
  // labels keep this charcoal color on error (no `Mui-error` overrides).
  // #genai Flush label to the input left edge (UX feedback: lightly indented First/Last labels).
  nameLabel: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: theme.evPalette.charcoal[800],
    marginBottom: '8px',
    marginLeft: 0,
    paddingLeft: 0,
    width: 'fit-content',
    position: 'inherit',
    top: 'inherit',
    left: 'inherit',
    transform: 'inherit',
  },

  asterisk: {
    color: theme.evPalette.rose[900],
  },

  nameInputText: {
    width: '100%',
    '& > .Mui-error > fieldset': {
      borderColor: `${theme.evPalette.rose[900]} !important`,
    },
  },

  nameInputHelperText: {
    // #genai Align name errors with the input edge, matching Company and Billing email.
    marginLeft: '0 !important',
    color: `${theme.evPalette.rose[900]} !important`,
  },

  // "Make this my default payment method" checkbox row — at card-padding level (no extra indent).
  // #genai Shown on mobile too (below Billing Information) so users can toggle the default on responsive screens.
  defaultCheckbox: {
    '& .myev-MuiCheckbox-root': {
      marginLeft: '-9px',
    },
    '& .myev-MuiTypography-root': {
      fontFamily: 'Noto Sans',
      fontSize: '14px',
      fontWeight: 400,
      color: '#000000',
    },
  },

  // Footer button row — Add Payment Method + Cancel, 304-wide with space-between.
  footer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '304px',
    // #genai Mobile (Figma 226:31979 — Order Summary block): stack full-width with the primary
    // "Add Payment Method" button on top + "Cancel" below. Gap 16 per Figma `gap-[16px]`.
    // 16/24 padding so the buttons match the section-wrapper rhythm.
    '@media (max-width: 639px)': {
      flexDirection: 'column',
      width: '100%',
      gap: '16px',
      padding: '16px 24px',
      boxSizing: 'border-box',
      // #genai Anchor the Save/Cancel group to the bottom of the full-height card so it sits just
      // above the global footer with the form content pinned to the top (Figma 269:21887). In State 2
      // the content fills the height, so this auto-margin collapses and the buttons follow the form.
      marginTop: 'auto',
    },
  },

  // "Add Payment Method" — disabled bg #E6EAF1 / text #B4BAC7; enabled bg #007CAD / white.
  addButton: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#007CAD',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#005A8D',
    },
    '&.Mui-disabled': {
      backgroundColor: '#E6EAF1',
      color: '#B4BAC7',
    },
    '@media (max-width: 639px)': {
      width: '100%',
    },
  },

  // "Cancel" — white bg, 1px #007CAD border, text #007CAD.
  cancelButton: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#FFFFFF',
    color: '#007CAD',
    border: '1px solid #007CAD',
    '&:hover': {
      backgroundColor: 'rgba(0,124,173,0.06)',
    },
    '@media (max-width: 639px)': {
      width: '100%',
    },
  },
}));
